<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps({
  report: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});
const { t, locale } = useI18n();
const money = value =>
  Number(value).toLocaleString(locale.value, {
    style: 'currency',
    currency: 'BRL',
  });
const label = key => t(`PROPOSAL_CONVERSION.${key}`);
const metrics = computed(() => [
  ['deals', label('DEALS')],
  ['proposed_deals', label('PROPOSED')],
  ['accepted_deals', label('ACCEPTED')],
  ['paid_deals', label('PAID')],
  ['acceptance_rate', label('ACCEPTANCE_RATE')],
  ['payment_rate', label('PAYMENT_RATE')],
  ['collected', label('COLLECTED')],
]);
const format = (key, value) => {
  if (key === 'collected') return money(value);
  if (key.endsWith('_rate')) return `${value}%`;
  return value;
};
</script>

<template>
  <section class="space-y-4" :aria-busy="isLoading">
    <div>
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ label('TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">{{ label('DESCRIPTION') }}</p>
      <p class="mt-1 text-xs text-n-slate-11">{{ label('DEFINITIONS') }}</p>
    </div>
    <p v-if="isLoading" role="status" class="text-sm text-n-slate-11">
      {{ label('LOADING') }}
    </p>
    <p v-else-if="!report" role="alert" class="text-sm text-n-ruby-11">
      {{ label('ERROR') }}
    </p>
    <template v-else>
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div
          v-for="[key, title] in metrics"
          :key="key"
          class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <p class="text-xs text-n-slate-11">{{ title }}</p>
          <p class="mt-1 text-xl font-semibold text-n-slate-12">
            {{ format(key, report.summary[key]) }}
          </p>
        </div>
      </div>
      <p class="text-xs text-n-slate-11">
        {{
          t('PROPOSAL_CONVERSION.DOCUMENTS', {
            conversations: report.summary.conversations,
            proposals: report.summary.proposals,
            shared: report.summary.shared_proposals,
            accepted: report.summary.accepted_proposals,
            payments: report.summary.paid_charges,
          })
        }}
      </p>
      <div
        v-for="[key, title] in [
          ['by_funnel', label('BY_FUNNEL')],
          ['by_source', label('BY_SOURCE')],
        ]"
        :key="key"
        class="overflow-x-auto rounded-xl border border-n-weak bg-n-solid-2"
      >
        <table class="w-full text-sm">
          <caption class="p-3 text-left font-medium text-n-slate-12">
            {{
              title
            }}
          </caption>
          <thead class="bg-n-solid-3 text-left text-n-slate-11">
            <tr>
              <th scope="col" class="p-3">{{ label('GROUP') }}</th>
              <th
                v-for="[metric, heading] in metrics"
                :key="metric"
                scope="col"
                class="p-3"
              >
                {{ heading }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in report[key]"
              :key="row.id ?? 'unattributed'"
              class="border-t border-n-weak text-n-slate-11"
            >
              <th scope="row" class="p-3 text-left font-medium text-n-slate-12">
                {{ row.name || label('UNATTRIBUTED') }}
              </th>
              <td v-for="[metric] in metrics" :key="metric" class="p-3">
                {{ format(metric, row[metric]) }}
              </td>
            </tr>
            <tr v-if="!report[key].length">
              <td :colspan="metrics.length + 1" class="p-4 text-n-slate-11">
                {{ label('EMPTY') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-xs text-n-slate-11">{{ label('EXPORT_NOTE') }}</p>
    </template>
  </section>
</template>
